import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Eye, EyeOff, Plus, Trash2, Search, Smartphone, Loader2 } from 'lucide-react';
import DateInput from '@/components/ui/date-input';
import { formatDate, formatDateTime, formatTime, formatTimeRangeUhr } from '@/lib/utils';

const inputCls = 'w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white';

export default function VeranstaltungenTab() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [onlyOffline, setOnlyOffline] = useState(false);
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [appMsgOpen, setAppMsgOpen] = useState(false);
  const [appMsgTitle, setAppMsgTitle] = useState('');
  const [appMsgBody, setAppMsgBody] = useState('');
  const [appMsgSending, setAppMsgSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/app-verwaltung/events', { params: { search: search || undefined } });
      setItems(r.data || []);
    } catch (e) { toast.error('Veranstaltungen konnten nicht geladen werden'); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const visible = items.filter((n) =>
    (!onlyOffline || n.status === 'offline') && (!onlyOnline || n.status === 'online'));

  const startNew = () => {
    setSel(null);
    setForm({ title: '', body: '', event_date: '', start_time: '', end_time: '', audience: 'public', push_enabled: true, cancelled: false, visible_from: '', visible_until: '' });
    setEditing(true);
  };
  const startEdit = () => {
    setForm({ title: sel.title, body: sel.body, event_date: sel.event_date || '', start_time: sel.start_time || '', end_time: sel.end_time || '', audience: sel.audience, push_enabled: sel.push_enabled, cancelled: sel.cancelled, visible_from: sel.visible_from || '', visible_until: sel.visible_until || '' });
    setEditing(true);
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Titel fehlt'); return; }
    const body = { ...form, event_date: form.event_date || null, start_time: form.start_time || null, end_time: form.end_time || null, visible_from: form.visible_from || null, visible_until: form.visible_until || null };
    try {
      const r = sel ? await api.put(`/admin/app-verwaltung/events/${sel.id}`, body)
        : await api.post('/admin/app-verwaltung/events', body);
      toast.success('Gespeichert'); setEditing(false); setSel(r.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  const toggle = async () => {
    try {
      const r = await api.post(`/admin/app-verwaltung/events/${sel.id}/${sel.status === 'online' ? 'unpublish' : 'publish'}`);
      setSel(r.data); load();
      toast.success(r.data.status === 'online' ? 'Freigegeben (Online)' : 'Offline genommen');
    } catch (e) { toast.error('Aktion fehlgeschlagen'); }
  };

  const toggleCancelled = async (checked) => {
    try {
      const r = await api.put(`/admin/app-verwaltung/events/${sel.id}`, {
        title: sel.title, body: sel.body, event_date: sel.event_date, start_time: sel.start_time,
        end_time: sel.end_time, audience: sel.audience, push_enabled: sel.push_enabled,
        cancelled: checked, visible_from: sel.visible_from, visible_until: sel.visible_until,
      });
      setSel(r.data); load();
      toast.success(checked ? 'Als abgesagt markiert' : 'Absage aufgehoben');
    } catch (e) { toast.error('Aktion fehlgeschlagen'); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/app-verwaltung/events/${sel.id}`); setSel(null); load(); toast.success('Gelöscht'); }
    catch (e) { toast.error('Löschen fehlgeschlagen'); }
  };

  const openAppMessage = () => {
    setAppMsgTitle(sel?.title || '');
    setAppMsgBody('');
    setAppMsgOpen(true);
  };

  const sendAppMessage = async () => {
    if (!sel?.id) return;
    if (!appMsgBody.trim()) { toast.error('Bitte einen Text eingeben'); return; }
    setAppMsgSending(true);
    try {
      await api.post(`/admin/app-verwaltung/events/${sel.id}/app-message`, {
        title: appMsgTitle.trim() || sel.title,
        body: appMsgBody.trim(),
      });
      toast.success('App-Nachricht an die Zielgruppe gesendet');
      setAppMsgOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'App-Nachricht fehlgeschlagen');
    } finally {
      setAppMsgSending(false);
    }
  };

  const fmt = (d) => (d ? formatDateTime(d) || '—' : '—');
  const fmtD = (d) => (d ? formatDate(d) : null);
  const when = (n) => n.event_date ? `${fmtD(n.event_date)}${n.start_time ? ` ${formatTimeRangeUhr(n.start_time, n.end_time)}` : ''}` : '';

  return (
    <>
    <div className="h-full flex">
      <div className="w-96 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-3 space-y-2 border-b border-slate-100">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyOffline} onChange={(e) => { setOnlyOffline(e.target.checked); setOnlyOnline(false); }} data-testid="events-filter-offline" /> Nur freizugebende anzeigen
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyOnline} onChange={(e) => { setOnlyOnline(e.target.checked); setOnlyOffline(false); }} data-testid="events-filter-online" /> Nur freigegebene anzeigen
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Schnellsuche" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="events-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.map((n) => (
            <button key={n.id} onClick={() => { setSel(n); setEditing(false); }}
                    data-testid={`events-item-${n.id}`}
                    className={`w-full text-left flex items-stretch border-b border-slate-100 ${sel?.id === n.id ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
              <span className="w-1.5 shrink-0" style={{ background: n.status === 'online' ? '#15803d' : '#ef4444' }} />
              <span className="flex-1 min-w-0 px-3 py-2.5">
                <span className="flex justify-between text-xs text-slate-400"><span>{n.cancelled ? '❌ Abgesagt' : ''}</span><span>{fmt(n.created_at)}</span></span>
                <span className="block text-sm font-medium truncate">{n.title}</span>
                <span className="block text-xs text-slate-500 truncate">{when(n)}</span>
              </span>
            </button>
          ))}
          {!visible.length && <p className="p-6 text-center text-sm text-slate-400">Keine Veranstaltungen</p>}
        </div>
        <p className="p-2 text-[11px] text-slate-400 border-t border-slate-100">Gefunden: {visible.length} / Gesamt: {items.length}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex justify-end mb-4 gap-2">
          {sel && !editing && <Button variant="outline" onClick={startEdit} data-testid="events-edit-btn">Bearbeiten</Button>}
          <Button onClick={startNew} className="bg-brand-red hover:bg-brand-red/90" data-testid="events-new-btn"><Plus className="w-4 h-4 mr-1" /> Neu</Button>
        </div>

        {editing ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 max-w-2xl">
            <input className={inputCls} placeholder="Titel" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="events-form-title" />
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-slate-500">Datum</label>
                <DateInput  className={inputCls} value={form.event_date} onChange={(e) => setForm((p) => ({ ...p, event_date: e.target.value }))} data-testid="events-form-date" /></div>
              <div><label className="text-xs text-slate-500">Beginn</label>
                <input type="time" className={inputCls} value={formatTime(form.start_time) || ''} onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))} data-testid="events-form-start" /></div>
              <div><label className="text-xs text-slate-500">Ende</label>
                <input type="time" className={inputCls} value={formatTime(form.end_time) || ''} onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))} data-testid="events-form-end" /></div>
            </div>
            <textarea className={inputCls} rows={8} placeholder="Beschreibung…" value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="events-form-body" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Sichtbar ab</label>
                <DateInput  className={inputCls} value={form.visible_from} onChange={(e) => setForm((p) => ({ ...p, visible_from: e.target.value }))} data-testid="events-form-from" /></div>
              <div><label className="text-xs text-slate-500">Sichtbar bis</label>
                <DateInput  className={inputCls} value={form.visible_until} onChange={(e) => setForm((p) => ({ ...p, visible_until: e.target.value }))} data-testid="events-form-until" /></div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.push_enabled} onChange={(e) => setForm((p) => ({ ...p, push_enabled: e.target.checked }))} data-testid="events-form-push" />
                Push-Nachricht bei Freigabe
              </label>
              <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white" value={form.audience} onChange={(e) => setForm((p) => ({ ...p, audience: e.target.value }))} data-testid="events-form-audience">
                <option value="public">Öffentlich</option>
                <option value="personal">Nur Personal</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={save} className="bg-brand-red hover:bg-brand-red/90" data-testid="events-form-save">Speichern</Button>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="events-form-cancel">Abbrechen</Button>
            </div>
          </div>
        ) : sel ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-2xl" data-testid="events-detail">
            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">{sel.audience === 'personal' ? 'Nur Personal' : 'Öffentlich'}</p>
            <h2 className="text-lg font-bold">{sel.title} {sel.cancelled && <span className="text-rose-600 text-sm">— ABGESAGT</span>}</h2>
            <p className="text-sm text-slate-500 mb-4">{when(sel)}</p>
            <p className="text-sm whitespace-pre-wrap text-slate-700">{sel.body}</p>
          </div>
        ) : (
          <p className="text-slate-400 text-sm mt-20 text-center">Veranstaltung links auswählen oder „Neu“ klicken</p>
        )}
      </div>

      {sel && !editing && (
        <div className="w-64 shrink-0 border-l border-slate-200 bg-white p-4 space-y-4">
          <Button onClick={toggle} className="w-full bg-brand-red hover:bg-brand-red/90" data-testid="events-toggle-btn">
            {sel.status === 'online' ? <><EyeOff className="w-4 h-4 mr-1" /> Offline nehmen</> : <><Eye className="w-4 h-4 mr-1" /> Freigeben</>}
          </Button>
          <Button variant="outline" onClick={openAppMessage} className="w-full" data-testid="events-app-message-btn">
            <Smartphone className="w-4 h-4 mr-1" /> App-Nachricht
          </Button>
          <div className="text-sm space-y-3">
            <div><p className="text-xs text-slate-400">Status</p>
              <p className={sel.status === 'online' ? 'text-brand-success font-semibold' : 'text-rose-600 font-semibold'}>{sel.status === 'online' ? 'Online' : 'Offline'}</p></div>
            <div><p className="text-xs text-slate-400">Push-Nachricht</p><p>{sel.push_enabled ? 'Ja' : 'Nein'}{sel.push_sent_at ? ` (gesendet ${fmt(sel.push_sent_at)})` : ''}</p></div>
            <div><p className="text-xs text-slate-400">Zeitraum</p><p>{fmtD(sel.visible_from) ? `Ab ${fmtD(sel.visible_from)}` : 'Sofort'}{fmtD(sel.visible_until) ? ` bis ${fmtD(sel.visible_until)}` : ''}</p></div>
            <div><p className="text-xs text-slate-400">Erstellung</p><p>{fmt(sel.created_at)}</p></div>
            <div><p className="text-xs text-slate-400">Änderung</p><p>{fmt(sel.updated_at)}</p></div>
            <label className="flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={sel.cancelled} onChange={(e) => toggleCancelled(e.target.checked)} data-testid="events-cancelled-checkbox" />
              Abgesagt
            </label>
          </div>
          <button onClick={remove} className="text-rose-500 text-sm flex items-center gap-1" data-testid="events-delete-btn"><Trash2 className="w-4 h-4" /> Löschen</button>
        </div>
      )}
    </div>

    <Dialog open={appMsgOpen} onOpenChange={setAppMsgOpen}>
      <DialogContent className="max-w-md" data-testid="events-app-message-dialog">
        <DialogHeader>
          <DialogTitle>App-Nachricht zur Veranstaltung</DialogTitle>
          <DialogDescription>
            Push an {sel?.audience === 'personal' ? 'Personal' : 'alle App-Nutzer'} dieser Veranstaltung.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={appMsgTitle}
            onChange={(e) => setAppMsgTitle(e.target.value)}
            placeholder="Titel"
            data-testid="events-app-message-title"
          />
          <textarea
            className={inputCls}
            rows={5}
            value={appMsgBody}
            onChange={(e) => setAppMsgBody(e.target.value)}
            placeholder="Nachricht…"
            data-testid="events-app-message-body"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAppMsgOpen(false)}>Abbrechen</Button>
            <Button onClick={() => sendAppMessage()} disabled={appMsgSending} data-testid="events-app-message-send">
              {appMsgSending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Smartphone className="h-4 w-4 mr-1.5" />}
              Senden
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
